/**
 * Wizard step 2: the activity set.
 *
 * The selection is capped at `LIMITS.maxActivitiesPerEvent` locally, matching
 * both the Worker validation and the recommendation engine, so the user cannot
 * build a draft the server will reject.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { ACTIVITIES, ACTIVITY_KEYS, type ActivityKey } from '@shared/activities'
import { LIMITS } from '@shared/constants'

import { ActivityRequirementPicker } from '../../../components/event/activity-requirement-picker'
import { ErrorAlert } from '../../../components/layout/states'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Chip, ChipGroup } from '../../../components/ui/chip'
import { WizardFooter } from '../../../components/layout/wizard'
import { activityLabelKey } from '../../../lib/format'
import { useEventDraft } from '../../../stores/drafts'

export function NewEventActivitiesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { draft, update } = useEventDraft()
  const [localError, setLocalError] = useState<string | null>(null)

  const atLimit = draft.activities.length >= LIMITS.maxActivitiesPerEvent

  function toggle(activity: ActivityKey) {
    const next = draft.activities.includes(activity)
      ? draft.activities.filter((value) => value !== activity)
      : atLimit
        ? draft.activities
        : ACTIVITY_KEYS.filter((key) => key === activity || draft.activities.includes(key))
    // Dropping an activity also drops its optional mark, so the two lists cannot
    // disagree about what the event declares.
    update({
      activities: next,
      optionalActivities: draft.optionalActivities.filter((value) => next.includes(value)),
    })
    setLocalError(null)
  }

  function goNext() {
    if (draft.activities.length === 0) {
      setLocalError(t('eventNew.activitiesError'))
      return
    }
    setLocalError(null)
    navigate('/me/events/new/weather')
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('eventNew.stepActivities')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <ChipGroup label={t('eventNew.stepActivities')}>
          {ACTIVITY_KEYS.map((activity) => (
            <Chip
              key={activity}
              selected={draft.activities.includes(activity)}
              disabled={atLimit && !draft.activities.includes(activity)}
              onClick={() => toggle(activity)}
            >
              <span aria-hidden>{ACTIVITIES[activity].icon}</span>
              {t(activityLabelKey(activity))}
            </Chip>
          ))}
        </ChipGroup>

        <p className="text-muted-foreground text-xs">
          {t('eventNew.activitiesHint', { max: LIMITS.maxActivitiesPerEvent })}
        </p>

        <ActivityRequirementPicker
          activities={draft.activities}
          optional={draft.optionalActivities}
          onChange={(optional) => {
            update({ optionalActivities: optional })
            setLocalError(null)
          }}
        />

        {localError ? (
          <ErrorAlert message={localError} />
        ) : null}

        <WizardFooter
          backTo="/me/events/new/title"
          backLabel={t('common.back')}
          nextLabel={t('common.next')}
          onNext={goNext}
        />
      </CardContent>
    </Card>
  )
}
